import { memo, useMemo } from "react";
import { CheckCircle2, Info } from "lucide-react";
import moment from "moment";
import { Typography, Button, Tooltip } from "@mui/material";

import { LocSubsStatus, SUBSCRIPTION_CONST } from "@/config/constants";
import useCustomerPortal from "@/helpers/hooks/useCustomerPortal";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import { getCreativePackageLabel } from "@/features/campaigns/digital/launch-digital-campaign/helpers/creativeLabel";
import {
  isTemporarilyClosedLocation,
  TemporaryClosedStatusNote,
} from "@/components/muiCustom/note/InlineStatusNote";

import BudgetRecommendationInfo from "@/features/campaigns/components/budgetRecommendation/BudgetRecommendationInfo";

type LocationHeaderProps = {
  locFormData?: Record<string, any>;
  locName: string;
  campaign?: Record<string, any>;
  isUnsubscribed?: boolean;
  is30DaysLeftToJoinCampaign?: boolean;
  subscriptionStatusType?: string;
  hideMeta?: boolean;
  isCreative?: boolean;
  loc?: Record<string, any>;
};

const NOT_SELECTED_LABEL = "Not selected";

const formatBudget = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

const getSelectedCreativeId = (creative: any) =>
  String(creative?.creative_id ?? creative?.id ?? creative);

const LocationHeader = ({
  locFormData = {},
  locName,
  campaign = {},
  isUnsubscribed,
  is30DaysLeftToJoinCampaign,
  subscriptionStatusType,
  hideMeta = false,
  isCreative,
  loc,
}: LocationHeaderProps) => {
  const { navigateToCustomerPortal } = useCustomerPortal();

  const locationBudget = useMemo(() => {
    const budget = locFormData?.budget;
    const brand_contribution_amount = locFormData?.brand_contribution_amount;
    const totalBudget = campaign?.brand_contribution
      ? Number(budget) + Number(brand_contribution_amount || 0)
      : budget;
    const numericBudget = Number(totalBudget);

    return Number.isFinite(numericBudget)
      ? formatBudget(numericBudget)
      : NOT_SELECTED_LABEL;
  }, [locFormData, campaign]);

  const budget = locFormData?.budget;

  const start_date = locFormData?.start_date;
  const end_date = locFormData?.end_date;

  const hasBudget = budget !== undefined && budget !== null && budget !== "";
  const hasBudgetRecommendation = Boolean(locFormData?.budgetRecommendation);
  const hasSelectedDates = Boolean(start_date && end_date);
  const selectedDateRange = hasSelectedDates
    ? `${moment(start_date).format("MMMM D, YYYY")} - ${moment(end_date).format(
        "MMMM D, YYYY"
      )}`
    : NOT_SELECTED_LABEL;
  const selectedPackageLabels = useMemo(() => {
    const selectedCreatives = Array.isArray(locFormData?.creatives)
      ? locFormData.creatives
      : [];

    if (!selectedCreatives.length) {
      return [];
    }

    const locationCreatives = Array.isArray(loc?.creatives)
      ? loc.creatives
      : [];

    return selectedCreatives.map((selectedCreative) => {
      const selectedCreativeId = getSelectedCreativeId(selectedCreative);
      const matchingCreative = locationCreatives.find(
        (creative) => getSelectedCreativeId(creative) === selectedCreativeId
      );

      return (
        getCreativePackageLabel(matchingCreative) ||
        getCreativePackageLabel(selectedCreative) ||
        selectedCreativeId
      );
    });
  }, [loc?.creatives, locFormData?.creatives]);
  const selectedPackageCount = selectedPackageLabels.length;
  const selectedPackageName = selectedPackageLabels[0] || NOT_SELECTED_LABEL;
  const selectedPackageTooltip =
    selectedPackageCount > 1 ? selectedPackageLabels.join("\n") : "";

  const renderMessage = useMemo(() => {
    let msg = SUBSCRIPTION_CONST.STATUS.UNSUBSCRIBED.TEXT;
    let lessThan30Msg;
    if (loc?.isPastDue) {
      msg = SUBSCRIPTION_CONST.STATUS.PAST_DUE.TEXT;
      lessThan30Msg = (
        <div>
          You must reactivate your subscription to launch this campaign.{" "}
          <div>
            <Button size="small" onClick={navigateToCustomerPortal}>
              Update Payment Information
            </Button>
          </div>
        </div>
      );
    } else if (loc?.isPaymentPending) {
      msg = SUBSCRIPTION_CONST.STATUS.PAYMENT_PENDING.TEXT;
      lessThan30Msg = (
        <div>
          The subscription payment is pending. Once done, this location will be
          available to join the campaign.
        </div>
      );
    } else if (subscriptionStatusType === LocSubsStatus.FALLOUT) {
      msg = SUBSCRIPTION_CONST.STATUS.FALLOUT.TEXT;
      if (is30DaysLeftToJoinCampaign) {
        lessThan30Msg =
          "You have less than a month left in your subscription for this location, please renew in order to join this campaign";
      }
    } else if (loc?.subscription !== null && isCreative === false) {
      msg = LocSubsStatus.IS_CREATIVE;
    }
    return (
      <span className="text-right">
        <div>
          <Info
            size={12}
            strokeWidth={2.4}
            className="mr-2"
            color={
              subscriptionStatusType === LocSubsStatus.UNSUBSCRIBED
                ? "#d32f2f"
                : "#f9a825"
            }
            aria-hidden="true"
          />
          {msg}
        </div>
        {lessThan30Msg && (
          <>
            <br />
            <small
              style={{ fontSize: "inherit" }}
              className="d-flex flex-column align-items-end"
            >
              {lessThan30Msg}
            </small>
          </>
        )}
      </span>
    );
  }, [
    subscriptionStatusType,
    is30DaysLeftToJoinCampaign,
    loc?.isPastDue,
    loc?.isPaymentPending,
    loc?.subscription,
    isCreative,
    navigateToCustomerPortal,
  ]);

  let wrapperClass = "unfilled";
  let icon = (
    <CheckCircle2
      className="location-filled-icon mr-2"
      size={24}
      aria-hidden="true"
    />
  );
  let locationName = (
    <div className="row">
      <div className="col-lg-6 mb-2 mb-lg-0 location-name">
        <Typography
          component="h3"
          variant="body2"
          className="location-name fw-b text-break"
        >
          {locName}
        </Typography>
        {isTemporarilyClosedLocation(loc) ? (
          <div className="mt-2">
            <TemporaryClosedStatusNote location={loc} />
          </div>
        ) : null}
      </div>
      <div className="col-lg-6 message d-flex align-items-center justify-content-lg-end">
        {isUnsubscribed && (
          <div className="card" style={{ display: "inline-block" }}>
            <span className="message">
              <Typography
                component="span"
                variant="caption"
                color={
                  subscriptionStatusType === LocSubsStatus.UNSUBSCRIBED
                    ? "error"
                    : "textPrimary"
                }
                className="fallOutside d-flex align-items-center fw-b"
              >
                {renderMessage}
              </Typography>
            </span>
          </div>
        )}
      </div>
    </div>
  );
  let locationFilledData = (
    <div className="location-meta">
      <div
        className={`location-meta-item budget${
          hasBudgetRecommendation ? " has-recommended-budget" : ""
        }`}
      >
        {hasBudgetRecommendation ? null : <span>Budget: </span>}
        {hasBudgetRecommendation ? (
          <span className="location-recommended-budget-tag">
            <BudgetRecommendationInfo
              recommendation={null}
              label="Recommended Budget"
            />
          </span>
        ) : null}
        {hasBudget ? locationBudget : NOT_SELECTED_LABEL}
      </div>
      <div className="location-meta-item date">
        <span>Start-End Date: </span>
        {selectedDateRange}
      </div>
      <div className="location-meta-item package">
        <span>Package: </span>
        {selectedPackageTooltip ? (
          <Tooltip
            title={
              <div className="location-package-tooltip">
                {selectedPackageLabels.map((packageLabel) => (
                  <div key={packageLabel}>{packageLabel}</div>
                ))}
              </div>
            }
            placement="top"
            arrow
          >
            <span className="location-package-summary">
              <span className="location-package-name">
                {selectedPackageName}
              </span>
              <span className="location-package-count">
                ({selectedPackageCount} Selected)
              </span>
            </span>
          </Tooltip>
        ) : (
          <span className="location-package-summary">
            <span className="location-package-name">{selectedPackageName}</span>
          </span>
        )}
      </div>
    </div>
  );

  if (joinCampaignHelper.isFullyFilledValidLocation(locFormData, campaign)) {
    wrapperClass = "filled";
  } else if (
    (locFormData.creatives && locFormData.creatives.length) ||
    locFormData.budget
    // locFormData.start_date ||
    // locFormData.end_date
  ) {
    wrapperClass = "partially-filled";
  }

  return (
    <div
      data-testid="location-header-content"
      className={`w-100 d-flex location-header-content ${wrapperClass}`}
      // onClick={(event) => event.stopPropagation()}
      onFocus={(event) => event.stopPropagation()}
    >
      {icon}
      <div className="w-100">
        {locationName}
        {hideMeta ? null : locationFilledData}
      </div>
    </div>
  );
};

export default memo(LocationHeader);
